import { ref, computed } from 'vue';
import type { CloseEndedQuestion } from '@ilteducation/exercise-service-types';
import { useDialogFocus } from '@ilteducation/components';
import type { SelectedVideo } from './useQuizterLoader';
import { useVideoQuestions } from './useVideoQuestions';

export function useVideoSelection(options?: { allowRemoveAll?: boolean; onChange?: () => void }) {
  const { allowRemoveAll = true, onChange } = options ?? {};

  const videos = ref<SelectedVideo[]>([]);
  const showAddVideoDialog = ref(false);
  const showEditDialog = ref(false);
  const editVideo = ref<SelectedVideo | undefined>();

  const { rememberFocus } = useDialogFocus([showAddVideoDialog, showEditDialog]);

  const openAddVideoDialog = () => {
    rememberFocus();
    showAddVideoDialog.value = true;
  };

  const excludedResourceIds = computed(() => videos.value.map((v) => v.resourceId));

  const handleAddVideo = async (video: SelectedVideo) => {
    videos.value.push(video);
    const { questions, fetch } = useVideoQuestions(video.resourceId);
    await fetch();
    const idx = videos.value.findIndex((v) => v.resourceId === video.resourceId);
    if (idx !== -1) {
      videos.value[idx] = {
        ...videos.value[idx],
        selectedQuestions: questions.value.length > 0 ? questions.value : undefined,
      };
    }
    onChange?.();
  };

  const handleRemoveVideo = (resourceId: string) => {
    if (allowRemoveAll || videos.value.length > 1) {
      videos.value = videos.value.filter((v) => v.resourceId !== resourceId);
      onChange?.();
    }
  };

  const handleEditVideoQuiz = (resourceId: string) => {
    rememberFocus();
    editVideo.value = videos.value.find((v) => v.resourceId === resourceId);
    showEditDialog.value = true;
  };

  const handleEditSave = (questions: CloseEndedQuestion[]) => {
    const idx = videos.value.findIndex((v) => v.resourceId === editVideo.value?.resourceId);
    if (idx !== -1) {
      videos.value[idx] = { ...videos.value[idx], selectedQuestions: questions };
      onChange?.();
    }
  };

  return {
    videos,
    excludedResourceIds,
    showAddVideoDialog,
    showEditDialog,
    editVideo,
    openAddVideoDialog,
    handleAddVideo,
    handleRemoveVideo,
    handleEditVideoQuiz,
    handleEditSave,
  };
}
